import { useId, type ReactNode } from "react";

import { cx } from "./controlStyles";
import { InfoTip } from "./InfoTip";

// Native checkbox tinted with the theme accent, with a clickable label and
// optional description. For settings that apply immediately use Switch.

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  /** A tooltip on an (i) beside the label (instead of a description line). */
  hint?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export function Checkbox({ checked, onChange, label, description, hint, disabled, id, className }: CheckboxProps) {
  const auto = useId();
  const boxId = id ?? `${auto}-check`;
  const labelId = `${auto}-label`;
  const descId = description ? `${auto}-desc` : undefined;
  return (
    <div className={cx("flex items-start gap-2.5", disabled && "opacity-45", className)}>
      <input
        id={boxId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        // Named by the label text alone; the description is announced once.
        aria-labelledby={labelId}
        aria-describedby={descId}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-[2px] h-4 w-4 shrink-0 cursor-pointer accent-[color:var(--c-accent)] disabled:cursor-default"
      />
      <label htmlFor={boxId} className={cx("min-w-0", disabled ? "cursor-default" : "cursor-pointer")}>
        {hint ? (
          <span className="flex items-center gap-1.5">
            <span id={labelId} className="text-sm text-fg">
              {label}
            </span>
            <InfoTip text={hint} />
          </span>
        ) : (
          <span id={labelId} className="block text-sm text-fg">
            {label}
          </span>
        )}
        {description && (
          <span id={descId} className="mt-0.5 block text-xs text-fg-muted">
            {description}
          </span>
        )}
      </label>
    </div>
  );
}
